// Phase 3D.2-B - Pure, deterministic GROWTH opportunity engine for the Savoie pilot.
//
// SCOPE / SAFETY CONTRACT:
//   * Pure analytical logic ONLY. Selects at most ONE growth opportunity from the
//     already-validated B4-B demo dataset ({ meta, records, disclaimer }).
//   * NO network / NO HTTP, NO DB, NO AI/provider, NO import side effects.
//   * Does NOT render anything, does NOT create a campaign, does NOT touch pages/routes.
//   * NO hardcoded market / figure in the engine: everything is derived from `records`.
//   * Reuses the pure KPI selectors from savoieKpis.js (no KPI formula is duplicated).
//   * Uses NON-ROUNDED values to filter and rank. Rounding is presentation-only.
//   * NO recovery logic, NO composite score, NO opaque weighting, NO assumed cause.
//
// Average length of stay and tourism spend per visitor are CONTEXT ONLY: they never
// influence eligibility, ranking, tie-breaking or selection.

import {
  MISSING,
  latestPeriod,
  arrivalsByMarket,
  growthByMarket,
  _internals,
} from "@/lib/savoieKpis";

const { METRIC, marketValue } = _internals;

// --- Pilot-specific thresholds -------------------------------------------
// IMPORTANT: these are thresholds SPECIFIC to the synthetic Savoie pilot demo.
// They are NOT a universal business rule and must be revisited per destination.
export const PILOT_MIN_GROWTH_PERCENT = 5.0;
export const PILOT_MIN_VISITOR_ARRIVALS = 10000;

// Deterministic ranking description surfaced in the output (documentation of the rule).
export const RANKING_METHOD =
  "growth_percent desc, visitor_arrivals desc, market_share_percent desc, market_code asc";

// Exact analytical disclaimer required for a detected opportunity (verbatim).
export const OPPORTUNITY_DISCLAIMER =
  "Opportunité analytique dérivée de données synthétiques de démonstration. " +
  "Elle ne constitue ni une observation réelle ni une recommandation client.";

// Deterministic reason tokens for the "none" result (never misleading).
export const NONE_REASONS = Object.freeze({
  INVALID_CONTRACT: "invalid_contract",
  NO_DATA: "no_data",
  NO_GROWTH_CANDIDATE: "no_growth_candidate",
});

// --- Presentation helpers (rounding is presentation-only) ----------------
const _fmtInt = (n) => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
const _fmtPct1 = (n) => (Math.round(n * 10) / 10).toFixed(1).replace(".", ",");
const _fmtPctSigned = (n) => (n >= 0 ? "+" : "") + _fmtPct1(n);

// --- Input contract gate (mirrors the frozen B4-B meta conditions) -------
function _contractValid(dataset) {
  if (!dataset || typeof dataset !== "object") return false;
  const meta = dataset.meta;
  if (!meta || typeof meta !== "object") return false;
  if (meta.is_demo !== true) return false;
  if (meta.provenance !== "synthetic_demo") return false;
  if (meta.provenance_label !== "DONNÉES DE DÉMONSTRATION") return false;
  if (meta.affiliation !== "none") return false;
  if (meta.simulation !== false) return false;
  if (!Array.isArray(dataset.records)) return false;
  return true;
}

// --- Deterministic candidate comparator (exported for isolated testing) --
// rows: { market, arrivals, share, growth } with NON-ROUNDED numeric values.
export function compareGrowthCandidates(a, b) {
  if (b.growth !== a.growth) return b.growth - a.growth;           // growth desc
  if (b.arrivals !== a.arrivals) return b.arrivals - a.arrivals;   // arrivals desc
  if (b.share !== a.share) return b.share - a.share;               // share desc
  if (a.market < b.market) return -1;                              // ISO2 asc
  if (a.market > b.market) return 1;
  return 0;
}

function _none(reason, meta) {
  return {
    status: "none",
    opportunity: null,
    reason,
    meta: meta || null,
    simulation: false,
  };
}

// Market display name strictly from the records (never hardcoded).
function _marketName(records, market) {
  for (const r of records) {
    if (r && r.source_market === market && r.source_market_name) {
      return r.source_market_name;
    }
  }
  return market; // fall back to the ISO2 code when no name is present in the data
}

// Currency only when explicitly present on a spend record for that market/period.
function _spendCurrency(records, market, year, month) {
  for (const r of records) {
    if (
      r &&
      r.metric_code === METRIC.SPEND &&
      r.source_market === market &&
      r.period &&
      r.period.reference_year === year &&
      r.period.month === month &&
      r.currency
    ) {
      return r.currency;
    }
  }
  return null;
}

/**
 * Select at most ONE growth opportunity from the validated demo dataset.
 * @param {{meta:object, records:Array, disclaimer:string}} dataset
 * @returns detected structure OR explicit { status: "none", ... }.
 */
export function selectGrowthOpportunity(dataset) {
  // 1. Fail-closed contract gate.
  if (!_contractValid(dataset)) {
    return _none(NONE_REASONS.INVALID_CONTRACT, null);
  }
  const meta = dataset.meta;
  const records = dataset.records;

  // 2. Latest period is determined dynamically from VISITOR_ARRIVALS.
  const period = latestPeriod(records);
  if (!period) return _none(NONE_REASONS.NO_DATA, meta);

  // 3. Arrivals per market + total at the latest period.
  const arrivals = arrivalsByMarket(records, period); // sorted desc, value !== null
  if (!arrivals.length) return _none(NONE_REASONS.NO_DATA, meta);
  const totalArrivals = arrivals.reduce((s, r) => s + r.value, 0);
  if (!(totalArrivals > 0)) return _none(NONE_REASONS.NO_DATA, meta);

  // 4. Growth vs the SAME month one year earlier.
  const growthList = growthByMarket(records, period);
  const growthMap = Object.create(null);
  for (const g of growthList) growthMap[g.market] = g.growthPct;

  // Build the per-market rows (NON-ROUNDED share/growth).
  const rows = arrivals.map(({ market, value }) => ({
    market,
    arrivals: value,
    share: (value / totalArrivals) * 100,
    growth: growthMap[market], // number OR MISSING token
  }));

  // 5. Identify and exclude the leader (max arrivals, ISO2 asc tie-break).
  let leader = null;
  for (const r of rows) {
    if (
      leader === null ||
      r.arrivals > leader.arrivals ||
      (r.arrivals === leader.arrivals && r.market < leader.market)
    ) {
      leader = r;
    }
  }
  const leaderMarket = leader ? leader.market : null;

  // 6. Eligibility: secondary market, growth available >= threshold, volume >= threshold.
  const eligible = rows.filter(
    (r) =>
      r.market !== leaderMarket &&
      typeof r.growth === "number" &&
      Number.isFinite(r.growth) &&
      r.growth >= PILOT_MIN_GROWTH_PERCENT &&
      r.arrivals >= PILOT_MIN_VISITOR_ARRIVALS
  );
  if (!eligible.length) return _none(NONE_REASONS.NO_GROWTH_CANDIDATE, meta);

  // 7. Deterministic ranking; 8. take the first only.
  eligible.sort(compareGrowthCandidates);
  const winner = eligible[0];

  // --- Evidence (raw, NON-ROUNDED) --------------------------------------
  const prevArrivals = marketValue(
    records,
    METRIC.ARRIVALS,
    winner.market,
    period.year - 1,
    period.month
  );

  // --- Context (computed AFTER selection; never used for selection) -----
  const overnight = marketValue(records, METRIC.OVERNIGHT, winner.market, period.year, period.month);
  const spend = marketValue(records, METRIC.SPEND, winner.market, period.year, period.month);
  const averageStay =
    overnight !== null && winner.arrivals > 0 ? overnight / winner.arrivals : MISSING;
  const spendPerVisitor =
    spend !== null && winner.arrivals > 0 ? spend / winner.arrivals : MISSING;
  const currency = _spendCurrency(records, winner.market, period.year, period.month);

  const marketName = _marketName(records, winner.market);
  const pad = (m) => String(m).padStart(2, "0");
  const periodStr = `${period.year}-${pad(period.month)}`;
  const comparisonStr = `${period.year - 1}-${pad(period.month)}`;
  const id = `growth:${winner.market}:${periodStr}`;

  const userFacingText =
    `Opportunité détectée : ${marketName} enregistre une croissance de ` +
    `${_fmtPctSigned(winner.growth)} % des arrivées par rapport au même mois de ` +
    `l'année précédente, avec ${_fmtInt(winner.arrivals)} visiteurs et une part de ` +
    `${_fmtPct1(winner.share)} % du total.`;

  return {
    status: "detected",
    id,
    type: "growth",
    market_code: winner.market,
    market_name: marketName,
    period: periodStr,
    comparison_period: comparisonStr,
    evidence: {
      visitor_arrivals: winner.arrivals,
      previous_year_visitor_arrivals: prevArrivals,
      growth_percent: winner.growth, // non-rounded
      market_share_percent: winner.share, // non-rounded
    },
    context: {
      average_stay: averageStay,
      tourism_spend_per_visitor: spendPerVisitor,
      currency,
    },
    thresholds: {
      min_growth_percent: PILOT_MIN_GROWTH_PERCENT,
      min_visitor_arrivals: PILOT_MIN_VISITOR_ARRIVALS,
    },
    ranking_method: RANKING_METHOD,
    meta, // provenance / meta preserved verbatim
    provenance: meta.provenance,
    simulation: false,
    disclaimer: OPPORTUNITY_DISCLAIMER,
    user_facing_text: userFacingText,
  };
}

export const _opportunityInternals = { _contractValid, _marketName, _spendCurrency };
